import { useEffect, useMemo, useRef, useState } from "react";
import {
  GRAPHICS_PRESETS,
  GRAPHICS_PRESET_ORDER,
  suggestPreset,
  type GraphicsPreset,
} from "@besenliga/shared";
import { browserStore } from "../creator/roster";
import type { BenchResult } from "./bench";
import { loadGraphics, saveGraphics } from "./graphics";
import { CAMERAS, StadiumView, type CameraMode } from "./view";

type Measured = BenchResult & { gpu: string; width: number; height: number };
interface Suggestion {
  from: GraphicsPreset;
  to: GraphicsPreset;
  fps: number;
}

const MODELS_URL = `${import.meta.env.BASE_URL}modelos/`;
const LABELS: Record<CameraMode, string> = {
  aerea: "Aérea (jogo)",
  tatica: "Tática",
  cinematica: "Cinemática",
};

/**
 * Tela do estádio. Parâmetros no endereço: `camera=tatica` e `t=6` fixam câmera e instante (capturas),
 * `atletas=0` mostra só o cenário, `medir=1` mede o desempenho e `graficos=baixo` força um preset.
 */
export function StadiumScreen() {
  const holderRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<StadiumView | null>(null);
  const store = useMemo(() => browserStore(), []);
  const params = useMemo(() => new URLSearchParams(location.hash.split("?")[1] ?? ""), []);
  const [graphics, setGraphics] = useState(() => {
    const saved = loadGraphics(store);
    const forced = params.get("graficos");
    return forced && (GRAPHICS_PRESET_ORDER as readonly string[]).includes(forced)
      ? { preset: forced as GraphicsPreset, measured: true }
      : saved;
  });
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [mode, setMode] = useState<CameraMode>("aerea");
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const [paused, setPaused] = useState(() => matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [state, setState] = useState<"carregando" | "pronto" | "erro">("carregando");
  const [info, setInfo] = useState({ calls: 0, triangles: 0, fps: 0 });
  const [stats, setStats] = useState("");
  const [bench, setBench] = useState<"parado" | "medindo" | Measured>("parado");

  const choosePreset = (preset: GraphicsPreset) => {
    const next = { preset, measured: true };
    setGraphics(next);
    saveGraphics(store, next);
    setSuggestion(null);
    setBench("parado");
  };

  const measure = () => {
    const view = viewRef.current;
    if (!view || bench === "medindo") return;
    setMode("aerea");
    setPaused(false);
    setBench("medindo");
    void view.measure().then(setBench);
  };

  useEffect(() => {
    const holder = holderRef.current;
    if (!holder) return;
    // Uma tela nova a cada montagem: cada visualizador tem o seu contexto WebGL (no modo estrito do React,
    // a montagem acontece duas vezes, e dividir o contexto estragava a sombra pré-calculada).
    const canvas = document.createElement("canvas");
    canvas.className = "stadium-canvas";
    canvas.setAttribute(
      "aria-label",
      "Estádio em 3D. Arraste para girar a câmera e use a roda do mouse para aproximar.",
    );
    holder.appendChild(canvas);
    let view: StadiumView;
    const preset = graphics.preset;
    try {
      view = new StadiumView(canvas, GRAPHICS_PRESETS[preset]);
    } catch {
      setState("erro");
      canvas.remove();
      return;
    }
    viewRef.current = view;
    setStats(JSON.stringify(view.stats()));
    setState("carregando");
    const cam = params.get("camera");
    if (cam && cam in CAMERAS) {
      view.setMode(cam as CameraMode);
      setMode(cam as CameraMode);
    } else view.setMode(modeRef.current);
    const t = params.get("t");
    if (t !== null) view.freezeAt(Number(t));
    let alive = true;
    // `atletas=0` mostra só o estádio (para medir o cenário sozinho).
    const athletes = params.get("atletas") === "0" ? Promise.resolve() : view.loadAthletes(MODELS_URL);
    athletes.then(
      () => {
        if (!alive) return;
        setState("pronto");
        // `medir=1` começa a medição sozinha assim que os atletas carregam.
        if (params.get("medir") === "1") {
          setBench("medindo");
          void view.measure().then((r) => alive && setBench(r));
        } else if (!graphics.measured && params.get("t") === null) {
          // Primeira visita: mede os primeiros 10 s sem mexer na tela e sugere um preset se precisar.
          void view.measure({ tour: false }).then((r) => {
            if (!alive) return;
            const done = { preset, measured: true };
            saveGraphics(store, done);
            setGraphics(done);
            const to = suggestPreset(preset, r.avgFps, r.low1Fps);
            if (to !== preset) setSuggestion({ from: preset, to, fps: r.avgFps });
          });
        }
      },
      (e: unknown) => {
        // O estádio continua visível sem os atletas; o erro fica no console para diagnóstico.
        console.error("Atletas não carregaram", e);
        if (alive) setState("pronto");
      },
    );
    const id = setInterval(() => setInfo({ ...view.info }), 500);
    return () => {
      alive = false;
      clearInterval(id);
      view.dispose();
      canvas.remove();
      viewRef.current = null;
    };
    // O preset muda o renderer (antialias, resolução, sombras): trocar de preset recria a cena.
  }, [graphics.preset]);

  useEffect(() => viewRef.current?.setPaused(paused), [paused]);

  return (
    <div className="stadium-screen" data-state={state} data-stats={stats}>
      <div ref={holderRef} className="stadium-holder" />
      <header className="stadium-bar">
        <a href="#" className="ghost">
          Menu
        </a>
        <h1>Estádio</h1>
      </header>
      <div className="stadium-controls">
        <div className="segmented" role="group" aria-label="Câmera">
          {(Object.keys(CAMERAS) as CameraMode[]).map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={mode === m}
              onClick={() => {
                setMode(m);
                viewRef.current?.setMode(m);
              }}
            >
              {LABELS[m]}
            </button>
          ))}
        </div>
        <button type="button" className="ghost" aria-pressed={paused} onClick={() => setPaused((p) => !p)}>
          {paused ? "Continuar" : "Pausar"}
        </button>
        <div className="segmented" role="group" aria-label="Gráficos">
          {GRAPHICS_PRESET_ORDER.map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={graphics.preset === p}
              onClick={() => graphics.preset !== p && choosePreset(p)}
            >
              {GRAPHICS_PRESETS[p].label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="ghost"
          onClick={measure}
          disabled={bench === "medindo" || state !== "pronto"}
        >
          {bench === "medindo" ? "Medindo por 10 s…" : "Medir desempenho"}
        </button>
      </div>
      {typeof bench === "object" && (
        <section className="bench-result" role="status" aria-label="Resultado da medição">
          <h2>{bench.avgFps} fps de média</h2>
          <p>
            1% mais lentos: {bench.low1Fps} fps · quadros acima de 20 ms: {Math.round(bench.slowShare * 100)}%
          </p>
          <p>
            {bench.width}×{bench.height} px · {bench.gpu} · preset {GRAPHICS_PRESETS[graphics.preset].label}
          </p>
          <div className="bench-actions">
            {(() => {
              const to = suggestPreset(graphics.preset, bench.avgFps, bench.low1Fps);
              return to === graphics.preset ? null : (
                <button type="button" className="primary" onClick={() => choosePreset(to)}>
                  Usar o preset {GRAPHICS_PRESETS[to].label}
                </button>
              );
            })()}
            <button type="button" className="ghost" onClick={() => setBench("parado")}>
              Fechar
            </button>
          </div>
        </section>
      )}
      {suggestion && (
        <section className="bench-result" role="status" aria-label="Sugestão de gráficos">
          <h2>Sugerimos o preset {GRAPHICS_PRESETS[suggestion.to].label}</h2>
          <p>
            Nos primeiros 10 s o jogo rodou a {suggestion.fps} fps no preset{" "}
            {GRAPHICS_PRESETS[suggestion.from].label}.
          </p>
          <div className="bench-actions">
            <button type="button" className="primary" onClick={() => choosePreset(suggestion.to)}>
              Usar o {GRAPHICS_PRESETS[suggestion.to].label}
            </button>
            <button type="button" className="ghost" onClick={() => setSuggestion(null)}>
              Manter o {GRAPHICS_PRESETS[suggestion.from].label}
            </button>
          </div>
        </section>
      )}
      <p className="stadium-info" aria-live="off">
        {info.fps} fps · {info.calls} chamadas de desenho · {Math.round(info.triangles / 1000)} mil triângulos
      </p>
      {state === "erro" && (
        <p className="stage-message" role="alert">
          Não foi possível mostrar o estádio em 3D neste navegador.
        </p>
      )}
    </div>
  );
}
